/**
 * TeleView Image Cache - IndexedDB-based caching for TV slideshow images
 * 
 * This service dramatically reduces backend reads by:
 * 1. Storing image blobs locally in IndexedDB
 * 2. Using md5Hash to detect changes without re-downloading
 * 3. Only syncing new/modified/deleted images
 */

const DB_NAME = "teleview-image-cache";
const DB_VERSION = 2; // Bumped to force re-download of HEIC images for conversion
const STORE_NAME = "images";

let dbInstance = null;

/**
 * Open/create the IndexedDB database
 * @returns {Promise<IDBDatabase>}
 */
export const openDB = () => {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error("IndexedDB open error:", request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        // Create object store with storagePath as key
        const store = db.createObjectStore(STORE_NAME, { keyPath: "storagePath" });
        // Index by masjidId for easy cleanup
        store.createIndex("masjidId", "masjidId", { unique: false });
      }
    };
  });
};

/**
 * Get a cached image by storage path
 * @param {string} storagePath 
 * @returns {Promise<{storagePath: string, masjidId: string, md5Hash: string, blob: Blob, name: string, uploadedAt: string} | null>}
 */
export const getImage = async (storagePath) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(storagePath);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("getImage error:", error);
    return null;
  }
};

/**
 * Save an image to the cache
 * @param {Object} imageData
 * @param {string} imageData.storagePath
 * @param {string} imageData.masjidId
 * @param {string} imageData.md5Hash
 * @param {Blob} imageData.blob
 * @param {string} imageData.name
 * @param {string} imageData.uploadedAt
 */
export const saveImage = async (imageData) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.put(imageData);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("saveImage error:", error);
    return false;
  }
};

/**
 * Delete an image from the cache
 * @param {string} storagePath
 */
export const deleteImage = async (storagePath) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(storagePath);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("deleteImage error:", error);
    return false;
  }
};

/**
 * Get all cached images for a specific masjid
 * @param {string} masjidId
 * @returns {Promise<Array<{storagePath: string, md5Hash: string, name: string, uploadedAt: string}>>}
 */
export const getCachedImagesForMasjid = async (masjidId) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const index = store.index("masjidId");
      const request = index.getAll(masjidId);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("getCachedImagesForMasjid error:", error);
    return [];
  }
};

/**
 * Clear all cached images for a specific masjid
 * @param {string} masjidId
 */
export const clearCacheForMasjid = async (masjidId) => {
  try {
    const cachedImages = await getCachedImagesForMasjid(masjidId);
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      
      let completed = 0;
      const total = cachedImages.length;
      
      if (total === 0) {
        resolve(true);
        return;
      }

      cachedImages.forEach((img) => {
        const request = store.delete(img.storagePath);
        request.onsuccess = () => {
          completed++;
          if (completed === total) resolve(true);
        };
        request.onerror = () => {
          completed++;
          if (completed === total) resolve(true);
        };
      });
    });
  } catch (error) {
    console.error("clearCacheForMasjid error:", error);
    return false;
  }
};

/**
 * Sync images with server - the main optimization function
 * 
 * Given server metadata, this function:
 * 1. Identifies new images that need downloading
 * 2. Identifies changed images (md5Hash mismatch)
 * 3. Identifies deleted images to remove from cache
 * 4. Returns lists for the caller to process
 * 
 * @param {string} masjidId
 * @param {Array<{storagePath: string, md5Hash: string, name: string, uploadedAt: string}>} serverMetadata
 * @returns {Promise<{toDownload: Array, toDelete: Array, cached: Array}>}
 */
export const syncWithServer = async (masjidId, serverMetadata) => {
  const cachedImages = await getCachedImagesForMasjid(masjidId);
  
  // Create lookup maps for efficient comparison
  const cachedMap = new Map(cachedImages.map(img => [img.storagePath, img]));
  const serverMap = new Map(serverMetadata.map(img => [img.storagePath, img]));
  
  const toDownload = [];
  const toDelete = [];
  const cached = [];

  // Check each server image
  for (const serverImg of serverMetadata) {
    const cachedImg = cachedMap.get(serverImg.storagePath);
    
    if (!cachedImg) {
      // New image - need to download
      toDownload.push(serverImg);
    } else if (!serverImg.md5Hash || !cachedImg.md5Hash) {
      // If either md5Hash is missing, we need to re-download to be safe
      toDownload.push(serverImg);
    } else if (cachedImg.md5Hash !== serverImg.md5Hash) {
      // Image changed - need to re-download
      toDownload.push(serverImg);
    } else {
      // Image unchanged - use from cache
      cached.push(cachedImg);
    }
  }

  // Check for deleted images (in cache but not on server)
  for (const cachedImg of cachedImages) {
    if (!serverMap.has(cachedImg.storagePath)) {
      toDelete.push(cachedImg);
    }
  }

  return { toDownload, toDelete, cached };
};

/**
 * Create object URLs from cached blobs
 * Returns an array of image objects with objectUrl for display
 * 
 * @param {Array<{storagePath: string, blob: Blob, name: string, uploadedAt: string}>} cachedImages
 * @returns {Array<{id: string, storagePath: string, name: string, uploadedAt: Date, url: string}>}
 */
export const createObjectUrlsFromCache = (cachedImages) => {
  return cachedImages.map(img => ({
    id: img.storagePath,
    storagePath: img.storagePath,
    name: img.name,
    uploadedAt: img.uploadedAt ? new Date(img.uploadedAt) : null,
    url: URL.createObjectURL(img.blob),
  }));
};
